// Sentry instrumentation for the React SPA.
// MUST be imported as the very first import in src/index.js so the SDK can
// hook into global error handlers before any other code runs.

import * as Sentry from '@sentry/react';

const dsn = process.env.REACT_APP_SENTRY_DSN;
const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-admin-secret']);
const SENSITIVE_TOKENS = ['password', 'secret', 'token', 'api_key', 'apikey'];

const scrub = (event) => {
  if (!event) return event;

  // Scrub request headers
  if (event.request && event.request.headers && typeof event.request.headers === 'object') {
    for (const key of Object.keys(event.request.headers)) {
      if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
        event.request.headers[key] = '[REDACTED]';
      }
    }
  }

  // Scrub request body fields
  const body = event.request && event.request.data;
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    for (const key of Object.keys(body)) {
      if (SENSITIVE_TOKENS.some((t) => key.toLowerCase().includes(t))) {
        body[key] = '[REDACTED]';
      }
    }
  }

  // Drop user email if present
  if (event.user && event.user.email) {
    delete event.user.email;
  }

  return event;
};

// If DSN is missing, Sentry.init({ dsn: undefined }) becomes a no-op. App starts fine.
Sentry.init({
  dsn: dsn || undefined,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
  release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask all text/inputs in replays — privacy first.
      maskAllText: true,
      blockAllMedia: false,
    }),
  ],

  // Performance — keep light in production.
  tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),

  // Session Replay — sample 0 normal, 1 on error.
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,

  // Propagate distributed traces to our backend.
  tracePropagationTargets: ['localhost', /^\/api\//, /\.preview\.emergentagent\.com\/api/, /realprop\.io\/api/],

  // Suppress known 3rd-party noise that we can't fix in source.
  // - PostHog session-recorder v1.434.12 PerformanceObserver clone bug
  //   (see /app/frontend/public/index.html for the runtime shim)
  ignoreErrors: [
    /PerformanceServerTiming/i,
    /could not be cloned.*postMessage/i,
    /Failed to execute 'postMessage' on 'Window'/i,
  ],
  denyUrls: [
    /us-assets\.i\.posthog\.com\/static\/.*posthog-recorder/i,
  ],

  beforeSend: scrub,
  beforeSendTransaction: scrub,
});
